import {createReducer, on} from '@ngrx/store';
import {CoreEnvironmentService} from '@core/services/environment/core-environment.service';
import {SharedChatState} from '@shared/components/chat/store/shared-chat.state';
import {SharedChatActions} from '@shared/components/chat/store/shared-chat.actions-type';
import {ChatMessageListDto, FileOrgaAttachmentDto, OrgaAttachmentListDto} from '@server-models';
import {ILocalAttachmentDto} from '@shared/components/attachments/interfaces/orga-attachment-dto-extend.interface';

function getInitialSharedChatState(): SharedChatState {
  const env = CoreEnvironmentService.getEnvironment();

  return {
    items: undefined,
    lastFetchedMessageId: 0,
    chat: undefined,
    isAccessDenied: false,
    isLoading: false
  };
}

export const sharedChatStore = createReducer(
  getInitialSharedChatState(),
  on(SharedChatActions.initChat, (state) => ({
    ...state,
    isLoading: true,
  })),
  on(SharedChatActions.initChatSuccess, (state, { chat }) => ({
    ...state,
    // only setting the id
    chat: chat,
  })),
  on(SharedChatActions.initChatFail, (state) => ({
    ...state,
    isLoading: false,
  })),
  on(SharedChatActions.getChat, (state) => ({
    ...state,
    isLoading: true,
  })),
  on(SharedChatActions.getChatSuccess, (state, { chat }) => ({
    ...state,
    chat: chat,
    isAccessDenied: false,
    isLoading: false,
  })),
  on(SharedChatActions.getChatFail, (state) => ({
    ...state,
    isAccessDenied: true,
    isLoading: false,
  })),
  on(SharedChatActions.chatMessageSend, (state) => ({
    ...state,
    isLoading: true,
  })),
  on(SharedChatActions.chatMessageSendFail, (state) => ({
    ...state,
    isLoading: false,
  })),
  on(SharedChatActions.chatMessageSendSuccess, (state, { newMessage }) => ({
    ...state,
    items: addOrReplaceMessages(state.items ?? [], [newMessage]),
    isLoading: false,
  })),
  on(SharedChatActions.addChatMember, (state) => ({
    ...state,
    isLoading: true,
  })),
  on(SharedChatActions.addChatMemberSuccess, (state, { newMember }) => ({
    ...state,
    chat: {
      ...state.chat,
      members: [...(state.chat?.chatMembers ?? []), newMember],
    },
    isLoading: false,
  })),
  on(SharedChatActions.addChatMemberFail, (state) => ({
    ...state,
    isLoading: false,
  })),
  on(SharedChatActions.addMe, (state) => ({
    ...state,
    isLoading: true,
  })),
  on(SharedChatActions.addMeSuccess, (state) => ({
    ...state,
    isLoading: false,
  })),
  on(SharedChatActions.addMeFail, (state) => ({
    ...state,
    isLoading: false,
  })),
  on(SharedChatActions.removeChatMember, (state) => ({
    ...state,
    isLoading: true,
  })),
  on(SharedChatActions.removeChatMemberSuccess, (state, { removedMember }) => ({
    ...state,
    chat: {
      ...state.chat,
      members: [
        ...(state.chat?.chatMembers ?? []).filter((member) => member.chatMemberId !== removedMember.chatMemberId),
      ],
    },
    isLoading: false,
  })),
  on(SharedChatActions.removeChatMemberFail, (state) => ({
    ...state,
    isLoading: false,
  })),
  on(SharedChatActions.removeMe, (state) => ({
    ...state,
    isLoading: true,
  })),
  on(SharedChatActions.removeMeSuccess, (state) => ({
    ...state,
    isLoading: false,
  })),
  on(SharedChatActions.removeMeFail, (state) => ({
    ...state,
    isLoading: false,
  })),
  on(SharedChatActions.deleteChatMessageSuccess, (state, { chatMessageId }) => ({
    ...state,
    items: state.items?.map(item => item.chatMessageId === chatMessageId ? { ...item, isDeleted: true } : item) ?? []
  })),
  on(SharedChatActions.clearChatStore, (state) => ({
    ...getInitialSharedChatState()
  })),
  on(SharedChatActions.initMessagePolling, (state) => ({
    ...state,
    items: [],
    lastFetchedMessageId: 0,
    isLoading: true,
  })),
  on(SharedChatActions.messagePollingSuccess, (state, { newItems }) => ({
    ...state,
    items: addOrReplaceMessages(state.items ?? [], newItems),
    lastFetchedMessageId: Math.max(state.lastFetchedMessageId ?? 0, getHighestMessageId(newItems)),
    isLoading: false
  }))
);

function getHighestMessageId(messages: ChatMessageListDto[]): number {
  return messages.reduce(
    (highestMessageId, message) => Math.max(highestMessageId, message.chatMessageId ?? 0),
    0
  );
}

function addOrReplaceMessages(
  existingMessages: ChatMessageListDto[],
  incomingMessages: ChatMessageListDto[]
): ChatMessageListDto[] {
  return incomingMessages.reduce((messages, incomingMessage) => {
    const normalizedMessage = normalizeMessage(incomingMessage);
    const existingMessageIndex = messages.findIndex(
      message => message.chatMessageId !== undefined &&
        message.chatMessageId === normalizedMessage.chatMessageId
    );

    if (existingMessageIndex === -1) {
      return [...messages, normalizedMessage];
    }

    return messages.map((message, messageIndex) =>
      messageIndex === existingMessageIndex ? normalizedMessage : message
    );
  }, [...existingMessages]);
}

function normalizeMessage(message: ChatMessageListDto): ChatMessageListDto {
  return {
    ...message,
    time: message.time && !message.time.endsWith('Z') ? `${message.time}Z` : message.time,
    attachments: normalizeAttachments(message.attachments)
  };
}

function normalizeAttachments(
  attachments: Array<OrgaAttachmentListDto | FileOrgaAttachmentDto> | null | undefined
): ILocalAttachmentDto[] {
  return (attachments ?? [])
    .map((attachment): ILocalAttachmentDto => {
      if ('file' in attachment || !('name' in attachment)) {
        const fileAttachment = attachment as FileOrgaAttachmentDto;

        return {
          attachmentId: fileAttachment.attachmentId,
          tenantId: fileAttachment.tenantId,
          type: fileAttachment.type,
          name: fileAttachment.file?.name ?? undefined,
          url: fileAttachment.file?.uri ?? undefined,
          mimeType: fileAttachment.file?.mimeType ?? undefined
        };
      }

      return {
        ...attachment,
        name: attachment.name ?? undefined,
        url: attachment.url ?? undefined,
        link: attachment.link ?? undefined,
        path: attachment.path ?? undefined
      };
    })
    .filter(attachment => Boolean(attachment.name));
}
